/**
 * v4.7.0 — Demo Mode toggle (sidebar entry) + persistent banner.
 *
 *   <DemoModeToggle />  — sits in the sidebar, only renders for users
 *                         with `can_access_demo_mode`. Two states:
 *                            🟢 Inactive: "Enter Demo Mode"
 *                            🟠 Active:   "Demo Mode Active — Exit"
 *                         Activation plays a brief premium transition.
 *
 *   <DemoModeBanner />  — thin persistent banner at the top of every
 *                         Operational Base page while Demo Mode is ON.
 *                         Visually honest: "Demo Environment · Blue
 *                         Flame Steakhouse — synthetic data, safe to
 *                         explore."
 *
 *   <DemoModeTransition /> — fixed overlay that animates the
 *                            activation/deactivation moment (~900ms).
 */
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Sparkles, Power, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useDemoMode } from "./DemoModeContext";

/* ── Toggle (sidebar) ─────────────────────────────────────────────── */
export const DemoModeToggle = () => {
    const { t } = useTranslation();
    const { active, canAccess, loaded, toggle } = useDemoMode();
    const [transitioning, setTransitioning] = useState(false);

    if (!loaded || !canAccess) return null;

    const handleClick = async () => {
        if (transitioning) return;
        setTransitioning(true);
        const next = await toggle();
        // Refresh the page-level data the moment Demo Mode flips so the
        // briefing/branding endpoints are re-queried and render with the
        // new lens immediately.
        window.dispatchEvent(new CustomEvent("ob:demo-mode-changed", {
            detail: { active: next },
        }));
        // Brief artificial delay so the transition overlay has a chance
        // to animate before the page rerenders.
        setTimeout(() => setTransitioning(false), 900);
        toast.success(next
            ? t("ob.demo_mode.activated", "Demo Environment active.")
            : t("ob.demo_mode.deactivated", "Returned to your operating environment."));
    };

    return (
        <button
            type="button"
            onClick={handleClick}
            disabled={transitioning}
            data-testid="ob-demo-mode-toggle"
            data-active={active ? "true" : "false"}
            className="w-full inline-flex items-center justify-between gap-2 rounded-xl px-3 py-2.5 text-sm transition-colors"
            style={{
                background: active
                    ? "linear-gradient(135deg, rgba(245, 158, 11, 0.14), rgba(245, 158, 11, 0.06))"
                    : "rgba(255,255,255,0.025)",
                border: active
                    ? "1px solid rgba(245, 158, 11, 0.42)"
                    : "1px solid rgba(255,255,255,0.08)",
                color: active ? "#FCD34D" : "var(--ob-text-secondary)",
                fontFamily: "Outfit, sans-serif",
            }}
        >
            <span className="inline-flex items-center gap-2 min-w-0">
                {active
                    ? <Power size={14} strokeWidth={1.75} />
                    : <Sparkles size={14} strokeWidth={1.75} />}
                <span className="truncate">
                    {active
                        ? t("ob.demo_mode.toggle_active", "Demo Mode — Exit")
                        : t("ob.demo_mode.toggle_enter", "Enter Demo Mode")}
                </span>
            </span>
            <span
                className="ob-demo-dot"
                data-active={active ? "true" : "false"}
                aria-hidden="true"
            />
        </button>
    );
};

/* ── Persistent banner ────────────────────────────────────────────── */
export const DemoModeBanner = () => {
    const { t } = useTranslation();
    const { active, loaded } = useDemoMode();
    if (!loaded || !active) return null;
    return (
        <div
            className="ob-demo-banner"
            role="status"
            data-testid="ob-demo-mode-banner"
        >
            <span className="ob-demo-banner-pulse" aria-hidden="true" />
            <ShieldCheck size={13} strokeWidth={1.75} className="ob-demo-banner-icon" />
            <span className="ob-demo-banner-text">
                {t("ob.demo_mode.banner", "Demo Environment · Blue Flame Steakhouse — synthetic data, safe to explore.")}
            </span>
        </div>
    );
};

/* ── Premium activation transition ────────────────────────────────── */
export const DemoModeTransition = () => {
    const { t: tr } = useTranslation();
    const { active, loaded } = useDemoMode();
    const [phase, setPhase] = useState(null);  // null | "entering" | "exiting"

    useEffect(() => {
        if (!loaded) return;
        const handler = (e) => {
            const becomingActive = !!e.detail?.active;
            setPhase(becomingActive ? "entering" : "exiting");
            // Total animation budget — ~900 ms, then dismiss.
            const t = setTimeout(() => setPhase(null), 900);
            return () => clearTimeout(t);
        };
        window.addEventListener("ob:demo-mode-changed", handler);
        return () => window.removeEventListener("ob:demo-mode-changed", handler);
    }, [loaded]);

    if (!phase) return null;

    return (
        <div
            className="ob-demo-transition"
            data-phase={phase}
            data-testid="ob-demo-mode-transition"
            aria-hidden="true"
        >
            {phase === "entering" && (
                <>
                    <div className="ob-demo-transition-wash" />
                    <img
                        src="/demo/blue_flame.png"
                        alt=""
                        className="ob-demo-transition-logo"
                    />
                    <p className="ob-demo-transition-label">{tr('operationalBase.enteringDemo')}</p>
                </>
            )}
            {phase === "exiting" && (
                <div className="ob-demo-transition-wash ob-demo-transition-wash-exit" />
            )}
        </div>
    );
};
